import { useParams, Link } from "react-router-dom";
import { useAuthor } from "@/hooks/useAuthor";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useCoffeePrice } from "@/hooks/useCoffeePrice";
import { useNostr } from "@nostrify/react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Coffee, Zap, Minus, Plus, ExternalLink, Sparkles, AlertCircle } from "lucide-react";
import { ZapButton } from "@/components/ZapButton";
import { Skeleton } from "@/components/ui/skeleton";
import { LoginArea } from "@/components/auth/LoginArea";
import { useSeoMeta } from "@unhead/react";
import { nip19, nip57 } from "nostr-tools";
import type { NostrMetadata, NostrEvent } from "@nostrify/nostrify";
import { useState, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { formatDistanceToNow } from "date-fns";

export default function CreatorPage() {
  const { npub } = useParams<{ npub: string }>();
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { coffeePriceSats, isLoading: priceLoading } = useCoffeePrice();
  const [coffeeCount, setCoffeeCount] = useState(1);
  const [customInput, setCustomInput] = useState("");
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [message, setMessage] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);

  let pubkey: string | undefined;
  try {
    if (npub) {
      const decoded = nip19.decode(npub);
      if (decoded.type === "npub") {
        pubkey = decoded.data;
      }
    }
  } catch (e) {
    console.error("Invalid npub:", e);
  }

  const author = useAuthor(pubkey || "");
  const metadata: NostrMetadata | undefined = author.data?.metadata;

  const displayName = metadata?.display_name || metadata?.name || "Anonymous";
  const about = metadata?.about || "";
  const truncatedAbout = about.length > 140 ? about.slice(0, 140).trim() + "..." : about;

  useSeoMeta({
    title: `${displayName} - ZapMeACoffee`,
    description: `Support ${displayName} with a Lightning tip. ${about.slice(0, 150)}`,
  });

  // Relays for zap queries
  const zapRelays = [
    'wss://relay.primal.net',
    'wss://relay.damus.io',
  ];

  // Fetch all zaps for this user from relays
  const { data: recentZaps, isLoading: loadingZaps } = useQuery({
    queryKey: ["zaps", pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      // Query relays for zap receipts
      const relayGroup = nostr.group(zapRelays);

      // Get all zap receipts for this pubkey
      const events = await relayGroup.query(
        [{ kinds: [9735], "#p": [pubkey] }],
        { signal }
      );

      // Parse zap data
      const zaps = events.map((event) => {
        const bolt11 = event.tags.find((t) => t[0] === "bolt11")?.[1];
        const descTag = event.tags.find((t) => t[0] === "description")?.[1];

        let senderPubkey: string | undefined;
        let comment = "";
        let amount = 0;

        // Parse zap request from description tag
        if (descTag) {
          try {
            const zapRequest = JSON.parse(descTag) as NostrEvent;
            senderPubkey = zapRequest.pubkey;
            comment = zapRequest.content || "";

            // Try to get amount from zap request's amount tag (in millisats)
            const amountTag = zapRequest.tags?.find((t: string[]) => t[0] === "amount")?.[1];
            if (amountTag) {
              amount = Math.floor(parseInt(amountTag) / 1000);
            }
          } catch {
            // ignore parse errors
          }
        }

        // If no amount from zap request, try bolt11
        if (amount === 0 && bolt11) {
          try {
            amount = nip57.getSatoshisAmountFromBolt11(bolt11);
          } catch {
            // ignore parse errors
          }
        }

        return {
          id: event.id,
          timestamp: event.created_at,
          senderPubkey,
          amount,
          comment,
        };
      });

      // Sort by timestamp descending (newest first)
      return zaps.sort((a, b) => b.timestamp - a.timestamp);
    },
    enabled: !!pubkey,
    refetchInterval: 30000,
    staleTime: 15000,
  });

  const zapAmount = coffeeCount * coffeePriceSats;

  if (!pubkey) {
    return (
      <div className="flex items-center justify-center p-4 py-16">
        <Card className="max-w-md w-full border-0 shadow-2xl shadow-black/10 rounded-3xl">
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary mb-4">
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">Invalid profile link</p>
            </div>
            <Button asChild variant="secondary" className="w-full h-12 rounded-2xl">
              <Link to="/">
                Back to Home
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (author.isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <Skeleton className="h-80 w-full rounded-3xl" />
            <Skeleton className="h-48 w-full rounded-3xl" />
          </div>
          <div className="lg:col-span-2">
            <Skeleton className="h-64 w-full rounded-3xl" />
          </div>
        </div>
      </div>
    );
  }

  const lud16 = metadata?.lud16;
  const isOwnPage = user?.pubkey === pubkey;

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="grid lg:grid-cols-5 gap-8">
        {/* Left Column - Tip Form & Recent Supporters */}
        <div className="lg:col-span-3 space-y-6">
          {/* Tip Form */}
          {lud16 ? (
            <Card className="border-0 shadow-xl shadow-black/5 rounded-3xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-lg" />
                    <div className="relative bg-gradient-to-br from-primary to-primary/80 p-3 rounded-2xl shadow-lg shadow-primary/25">
                      <Zap className="h-5 w-5 text-primary-foreground fill-primary-foreground" />
                    </div>
                  </div>
                  <h2 className="text-xl font-semibold tracking-tight">
                    Buy {displayName} a coffee
                  </h2>
                </div>

                {/* Coffee Counter */}
                <div className="bg-secondary/50 rounded-3xl p-8 mb-6">
                  <div className="flex items-center justify-center gap-6">
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-14 w-14 rounded-2xl shadow-sm"
                      onClick={() => setCoffeeCount(Math.max(1, coffeeCount - 1))}
                      disabled={coffeeCount <= 1}
                    >
                      <Minus className="h-5 w-5" />
                    </Button>

                    <div className="text-center min-w-[140px]">
                      <div className="flex items-center justify-center gap-1 text-4xl mb-2">
                        {[...Array(Math.min(coffeeCount, 5))].map((_, i) => (
                          <span key={i}>☕</span>
                        ))}
                        {coffeeCount > 5 && <span className="text-2xl ml-1 text-muted-foreground">+{coffeeCount - 5}</span>}
                      </div>
                      <p className="text-lg font-semibold">
                        {coffeeCount} {coffeeCount === 1 ? "coffee" : "coffees"}
                      </p>
                      <p className="text-sm text-primary font-medium mt-1">
                        {priceLoading ? (
                          <span className="animate-pulse">Loading price...</span>
                        ) : (
                          `${zapAmount.toLocaleString()} sats`
                        )}
                      </p>
                    </div>

                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-14 w-14 rounded-2xl shadow-sm"
                      onClick={() => setCoffeeCount(coffeeCount + 1)}
                    >
                      <Plus className="h-5 w-5" />
                    </Button>
                  </div>

                  {/* Quick select */}
                  <div className="flex justify-center items-center gap-2 mt-6">
                    {[1, 3, 5, 10].map((num) => (
                      <Button
                        key={num}
                        variant={coffeeCount === num && !isCustomMode ? "default" : "secondary"}
                        size="sm"
                        className={`rounded-xl px-5 ${
                          coffeeCount === num && !isCustomMode
                            ? "bg-primary hover:bg-primary/90 shadow-md shadow-primary/25"
                            : ""
                        }`}
                        onClick={() => {
                          setCoffeeCount(num);
                          setIsCustomMode(false);
                          setCustomInput("");
                        }}
                      >
                        {num}
                      </Button>
                    ))}
                    {isCustomMode ? (
                      <Input
                        ref={customInputRef}
                        type="number"
                        min="1"
                        placeholder="..."
                        value={customInput}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCustomInput(val);
                          const num = parseInt(val);
                          if (!isNaN(num) && num >= 1) {
                            setCoffeeCount(num);
                          }
                        }}
                        onBlur={() => {
                          if (!customInput || parseInt(customInput) < 1) {
                            setIsCustomMode(false);
                            setCoffeeCount(1);
                          }
                        }}
                        className="w-16 h-8 text-center text-sm rounded-xl border-0 bg-primary text-primary-foreground shadow-md shadow-primary/25 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="rounded-xl px-4"
                        onClick={() => {
                          setIsCustomMode(true);
                          setCustomInput("");
                          // Focus the input after it renders
                          setTimeout(() => customInputRef.current?.focus(), 0);
                        }}
                      >
                        ...
                      </Button>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div className="mb-6">
                  <Textarea
                    placeholder="Say something nice... (optional)"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="border-0 bg-secondary/50 rounded-2xl resize-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0"
                    rows={3}
                  />
                </div>

                {/* Zap Button */}
                {user ? (
                  <ZapButton
                    pubkey={pubkey}
                    amount={zapAmount}
                    comment={message}
                    variant="default"
                    className="w-full h-14 text-lg rounded-2xl bg-primary hover:bg-primary/90 shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={priceLoading || coffeePriceSats === 0}
                    onSuccess={() => {
                      setMessage("");
                      setCoffeeCount(1);
                    }}
                  >
                    <Zap className="mr-2 h-5 w-5 fill-current" />
                    {priceLoading ? "Loading price..." : `Send ${zapAmount.toLocaleString()} sats ⚡`}
                  </ZapButton>
                ) : (
                  <div className="bg-secondary/50 p-8 rounded-2xl text-center">
                    <p className="text-sm text-muted-foreground mb-5">
                      Log in to send a tip
                    </p>
                    <LoginArea className="w-full justify-center" />
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-dashed border-border/60 shadow-none rounded-3xl">
              <CardContent className="py-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary mb-5">
                  <Zap className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">
                  {isOwnPage ? (
                    <>
                      Add a Lightning address to start receiving tips!{" "}
                      <Link to="/" className="text-primary hover:underline underline-offset-2 font-medium">
                        Edit profile
                      </Link>
                    </>
                  ) : (
                    "This creator hasn't set up Lightning tips yet."
                  )}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Recent Supporters - Now in Left Column */}
          <Card className="border-0 shadow-xl shadow-black/5 rounded-3xl overflow-hidden">
            <CardHeader className="pb-4 border-b border-border/50">
              <CardTitle className="flex items-center gap-2.5 text-lg font-semibold">
                <div className="p-1.5 rounded-lg bg-primary/10">
                  <Zap className="h-4 w-4 text-primary fill-primary" />
                </div>
                Recent Supporters
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5">
              {loadingZaps ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-11 w-11 rounded-2xl" />
                      <div className="flex-1 space-y-1.5 min-w-0">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentZaps && recentZaps.length > 0 ? (
                <div className="space-y-3">
                  {recentZaps.map((zap) => (
                    <ZapItem key={zap.id} zap={zap} coffeePriceSats={coffeePriceSats} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-secondary mb-4">
                    <Sparkles className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No tips yet. Be the first to support!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Profile Card */}
        <div className="lg:col-span-2">
          <Card className="border-0 shadow-xl shadow-black/5 rounded-3xl sticky top-20 overflow-hidden">
            <CardContent className="p-6">
              {/* Profile Info */}
              <div className="flex flex-col items-center text-center">
                <Avatar className="h-24 w-24 ring-4 ring-background shadow-xl mb-4">
                  <AvatarImage src={metadata?.picture} className="object-cover" />
                  <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary text-2xl font-semibold">
                    {displayName[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <h1 className="text-xl font-semibold tracking-tight truncate max-w-full">{displayName}</h1>
                {metadata?.nip05 && (
                  <p className="text-sm text-primary truncate max-w-full">{metadata.nip05}</p>
                )}
              </div>

              {truncatedAbout && (
                <p className="text-muted-foreground leading-relaxed mt-4 text-sm text-center">{truncatedAbout}</p>
              )}

              {metadata?.website && (
                <a
                  href={metadata.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary hover:underline underline-offset-2 w-full justify-center"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {metadata.website.replace(/^https?:\/\//, "")}
                </a>
              )}

              {isOwnPage && (
                <Button asChild variant="secondary" className="w-full mt-4 rounded-xl">
                  <Link to="/">Edit Profile</Link>
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ZapItem({ zap, coffeePriceSats }: { zap: { id: string; timestamp: number; senderPubkey?: string; amount: number; comment: string }; coffeePriceSats: number }) {
  const author = useAuthor(zap.senderPubkey || "");
  const metadata = author.data?.metadata;
  const displayName = metadata?.display_name || metadata?.name || "Anonymous";
  const coffees = coffeePriceSats > 0 ? Math.round(zap.amount / coffeePriceSats) : 1;
  const npub = zap.senderPubkey ? nip19.npubEncode(zap.senderPubkey) : null;

  return (
    <div className="flex items-start gap-3 p-4 bg-secondary/30 rounded-2xl hover:bg-secondary/50 transition-colors">
      {npub ? (
        <Link to={`/${npub}`}>
          <Avatar className="h-11 w-11 rounded-xl ring-2 ring-border/30 hover:ring-primary/50 transition-all">
            <AvatarImage src={metadata?.picture} className="object-cover" />
            <AvatarFallback className="rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary text-sm font-semibold">
              {displayName[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
      ) : (
        <Avatar className="h-11 w-11 rounded-xl ring-2 ring-border/30">
          <AvatarImage src={metadata?.picture} className="object-cover" />
          <AvatarFallback className="rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary text-sm font-semibold">
            {displayName[0].toUpperCase()}
          </AvatarFallback>
        </Avatar>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {npub ? (
            <Link
              to={`/${npub}`}
              className="font-medium truncate hover:text-primary transition-colors"
            >
              {displayName}
            </Link>
          ) : (
            <span className="font-medium truncate">{displayName}</span>
          )}
          <span className="text-primary text-sm font-medium flex-shrink-0 bg-primary/10 px-2 py-0.5 rounded-lg">
            ☕ × {coffees || 1}
          </span>
        </div>
        {zap.comment && (
          <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed">{zap.comment}</p>
        )}
        <p className="text-xs text-muted-foreground/70 mt-2">
          {formatDistanceToNow(zap.timestamp * 1000, { addSuffix: true })}
        </p>
      </div>
    </div>
  );
}
